import { useState, useEffect } from 'react';
import { getAllSubscriptions } from '../../services/subscriptionService';
import { UnifiedSubscription } from '../../types/subscription';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

interface PlatformMetrics {
  ios: {
    total: number;
    active: number;
    trialing: number;
    canceled: number;
    test: number;
    production: number;
  };
  android: {
    total: number;
    active: number;
    trialing: number;
    canceled: number;
    test: number;
    production: number;
  };
  stripe: {
    total: number;
    active: number;
    trialing: number;
    canceled: number;
  };
}

const MobilePlatformTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<UnifiedSubscription[]>([]);
  const [metrics, setMetrics] = useState<PlatformMetrics>({
    ios: { total: 0, active: 0, trialing: 0, canceled: 0, test: 0, production: 0 },
    android: { total: 0, active: 0, trialing: 0, canceled: 0, test: 0, production: 0 },
    stripe: { total: 0, active: 0, trialing: 0, canceled: 0 },
  });
  const [environmentFilter, setEnvironmentFilter] = useState<'all' | 'test' | 'production'>('all');

  useEffect(() => {
    const loadData = async () => {
      try {
        let data = await getAllSubscriptions();
        
        // Filter by environment if needed
        if (environmentFilter !== 'all') {
          data = data.filter((sub) => sub.environment === environmentFilter);
        }

        // Keep all for platform breakdown; filter for table to mobile only
        const allSubs = data;
        const mobileOnly = data.filter((sub) => sub.platform === 'ios' || sub.platform === 'android');
        setSubscriptions(mobileOnly);

        // Calculate metrics for all three platforms
        const iosSubs = allSubs.filter((sub) => sub.platform === 'ios');
        const androidSubs = allSubs.filter((sub) => sub.platform === 'android');
        const stripeSubs = allSubs.filter((sub) => sub.platform === 'stripe');

        const calculateMobileMetrics = (subs: UnifiedSubscription[]) => ({
          total: subs.length,
          active: subs.filter((s) => s.status === 'active').length,
          trialing: subs.filter((s) => s.status === 'trialing').length,
          canceled: subs.filter((s) => s.status === 'canceled').length,
          test: subs.filter((s) => s.environment === 'test').length,
          production: subs.filter((s) => s.environment === 'production').length,
        });
        const calculateStripeMetrics = (subs: UnifiedSubscription[]) => ({
          total: subs.length,
          active: subs.filter((s) => s.status === 'active').length,
          trialing: subs.filter((s) => s.status === 'trialing').length,
          canceled: subs.filter((s) => s.status === 'canceled').length,
        });

        setMetrics({
          ios: calculateMobileMetrics(iosSubs),
          android: calculateMobileMetrics(androidSubs),
          stripe: calculateStripeMetrics(stripeSubs),
        });
      } catch (error) {
        console.error('Error loading mobile platform data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [environmentFilter]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading mobile platform insights...</p>
      </div>
    );
  }

  // Calculate conversion rates
  const iosTrialConversion = metrics.ios.trialing > 0
    ? ((metrics.ios.active - metrics.ios.trialing) / metrics.ios.trialing) * 100
    : 0;
  const androidTrialConversion = metrics.android.trialing > 0
    ? ((metrics.android.active - metrics.android.trialing) / metrics.android.trialing) * 100
    : 0;

  // Calculate churn rates
  const iosChurnRate = metrics.ios.total > 0
    ? (metrics.ios.canceled / metrics.ios.total) * 100
    : 0;
  const androidChurnRate = metrics.android.total > 0
    ? (metrics.android.canceled / metrics.android.total) * 100
    : 0;

  // Platform distribution chart (Web/Stripe + iOS + Android)
  const platformDistributionData = {
    labels: ['Web (Stripe)', 'iOS', 'Android'],
    datasets: [
      {
        label: 'Total Subscriptions',
        data: [metrics.stripe.total, metrics.ios.total, metrics.android.total],
        backgroundColor: ['#2563eb', '#8b5cf6', '#10b981'],
      },
    ],
  };

  // Environment distribution chart
  const environmentData = {
    labels: ['Production', 'Test'],
    datasets: [
      {
        label: 'iOS',
        data: [metrics.ios.production, metrics.ios.test],
        backgroundColor: '#8b5cf6',
      },
      {
        label: 'Android',
        data: [metrics.android.production, metrics.android.test],
        backgroundColor: '#10b981',
      },
    ],
  };

  // Status comparison chart (all three platforms)
  const statusComparisonData = {
    labels: ['Active', 'Trialing', 'Canceled'],
    datasets: [
      { label: 'Web (Stripe)', data: [metrics.stripe.active, metrics.stripe.trialing, metrics.stripe.canceled], backgroundColor: '#2563eb' },
      { label: 'iOS', data: [metrics.ios.active, metrics.ios.trialing, metrics.ios.canceled], backgroundColor: '#8b5cf6' },
      { label: 'Android', data: [metrics.android.active, metrics.android.trialing, metrics.android.canceled], backgroundColor: '#10b981' },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Platform Insights (Web + iOS + Android)
      </h1>

      {/* Environment Filter */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ marginRight: '1rem', fontWeight: 'bold' }}>Environment:</label>
        <select
          value={environmentFilter}
          onChange={(e) => setEnvironmentFilter(e.target.value as 'all' | 'test' | 'production')}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="all">All Environments</option>
          <option value="production">Production Only</option>
          <option value="test">Test Only</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        {/* iOS Metrics */}
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #8b5cf6',
        }}>
          <h3 style={{ marginTop: 0, color: '#8b5cf6' }}>iOS 📱</h3>
          <div style={{ marginTop: '1rem' }}>
            <p style={{ margin: '0.5rem 0' }}><strong>Total:</strong> {metrics.ios.total}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Active:</strong> {metrics.ios.active}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Trialing:</strong> {metrics.ios.trialing}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Canceled:</strong> {metrics.ios.canceled}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Production:</strong> {metrics.ios.production}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Test:</strong> {metrics.ios.test}</p>
            <p style={{ margin: '0.5rem 0', color: '#10b981' }}>
              <strong>Trial Conversion:</strong> {iosTrialConversion.toFixed(1)}%
            </p>
            <p style={{ margin: '0.5rem 0', color: '#ef4444' }}>
              <strong>Churn Rate:</strong> {iosChurnRate.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Android Metrics */}
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #10b981',
        }}>
          <h3 style={{ marginTop: 0, color: '#10b981' }}>Android 🤖</h3>
          <div style={{ marginTop: '1rem' }}>
            <p style={{ margin: '0.5rem 0' }}><strong>Total:</strong> {metrics.android.total}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Active:</strong> {metrics.android.active}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Trialing:</strong> {metrics.android.trialing}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Canceled:</strong> {metrics.android.canceled}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Production:</strong> {metrics.android.production}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Test:</strong> {metrics.android.test}</p>
            <p style={{ margin: '0.5rem 0', color: '#10b981' }}>
              <strong>Trial Conversion:</strong> {androidTrialConversion.toFixed(1)}%
            </p>
            <p style={{ margin: '0.5rem 0', color: '#ef4444' }}>
              <strong>Churn Rate:</strong> {androidChurnRate.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Web (Stripe) Metrics */}
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #2563eb',
        }}>
          <h3 style={{ marginTop: 0, color: '#2563eb' }}>Web (Stripe) 🌐</h3>
          <div style={{ marginTop: '1rem' }}>
            <p style={{ margin: '0.5rem 0' }}><strong>Total:</strong> {metrics.stripe.total}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Active:</strong> {metrics.stripe.active}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Trialing:</strong> {metrics.stripe.trialing}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Canceled:</strong> {metrics.stripe.canceled}</p>
            <p style={{ margin: '0.5rem 0', fontSize: '0.8rem', color: '#666' }}>
              Web portal subscriptions via Stripe; single-session & callables.
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Platform Distribution</h3>
          <PieChart data={platformDistributionData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Environment Distribution</h3>
          <BarChart data={environmentData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Status Comparison</h3>
          <BarChart data={statusComparisonData} height={300} />
        </div>
      </div>

      {/* Recent Subscriptions Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginTop: '2rem',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Recent Mobile Subscriptions
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Platform</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Environment</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Product ID</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.slice(0, 50).map((sub, index) => (
              <tr key={`${sub.userId}-${sub.platform}-${index}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{sub.email || sub.userId}</td>
                <td style={{ padding: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: sub.platform === 'ios' ? '#f3e5f5' : '#fff3e0',
                    fontSize: '0.875rem',
                  }}>
                    {sub.platform.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: sub.status === 'active' ? '#d1fae5' : sub.status === 'trialing' ? '#dbeafe' : '#fee2e2',
                    color: sub.status === 'active' ? '#065f46' : sub.status === 'trialing' ? '#1e40af' : '#991b1b',
                    fontSize: '0.875rem',
                  }}>
                    {sub.status}
                  </span>
                </td>
                <td style={{ padding: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: sub.environment === 'production' ? '#d1fae5' : '#fef3c7',
                    color: sub.environment === 'production' ? '#065f46' : '#92400e',
                    fontSize: '0.875rem',
                  }}>
                    {sub.environment || 'production'}
                  </span>
                </td>
                <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{sub.productId || 'N/A'}</td>
                <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                  {sub.createdAt.toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {subscriptions.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          No mobile subscriptions found
        </div>
      )}
    </div>
  );
};

export default MobilePlatformTab;

